import { useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getContracts } from 'modules/contracts/actions';
import Carousel from 'components/UI/Carousel';
import { ROUTE_PATH } from 'routes';
import { getSections } from 'modules/sections/actions';
import Box from 'components/UI/Box/Box';
import Button from 'components/UI/Button';
import { ReactComponent as DocumentIcon } from 'assets/icons/document.svg';
import PaymentBox from 'components/PaymentBox';
import useLiterals from 'utils/hooks/useLiterals';

const LIMIT_LENGTH = 4;

const ContractsAndSections = () => {
  const literals = useLiterals('home');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const contracts = useSelector((state) => state.contracts);
  const sections = useSelector((state) => state.sections);
  const urlParams = new URLSearchParams(window.location.search);

  const items = useMemo(() => {
    const auxItems = [
      ...(contracts?.list || []),
      ...(sections?.list || []),
    ];

    auxItems.sort((a, b) => a.auxOrder - b.auxOrder);
    return auxItems;
  }, [contracts, sections]);

  useEffect(() => {
    if (!contracts.loaded || contracts.needUpdate) {
      dispatch(getContracts());
    }
    if (!sections.loaded || sections.needUpdate) {
      dispatch(getSections());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSlideChange = (e) => {
    const contract = contracts?.list[e.activeIndex]?.Numero;
    if (contract) {
      navigate(`${ROUTE_PATH.HOME}?contract=${contract}`);
    }
  };

  const renderItems = () => {
    if (!items.length) return null;
    const slides = [];

    items.forEach((item) => {
      if (slides.length === LIMIT_LENGTH) {
        // eslint-disable-next-line no-useless-return
        return;
      }
      slides.push(
        <PaymentBox
          type={item.auxType}
          data={item}
          actionText={literals.details}
          showCoverage={false}
        />,
      );
    });
    let title = literals.yourContractsAndSections;
    if (!contracts?.list?.length) {
      title = literals.yourSections;
    } else if (!sections?.list?.length) {
      title = literals.yourContracts;
    }
    if (items.length > LIMIT_LENGTH) {
      slides.push((
        <Box className='deadlines-box deadline-show-more mb-4' padding='sm'>
          <Link to={ROUTE_PATH.PAYMENTS} className='no-underline text-center block'>
            <div className='p-4'>
              <div className='flex items-center justify-start gap-2 mb-6'>
                <DocumentIcon />
                <p className='h3 text-neutral-800 text-left mb-0'>{literals.numActiveContracts}</p>
              </div>
              <div className='px-6 py-2'>
                <div className='flex justify-between items-center mb-2'>
                  <p className='h3 text-neutral-800 font-normal mb-0'>{literals.contracts}</p>
                  <p className='h1 text-neutral-800 m-0'>{contracts?.list?.length}</p>
                </div>
                <div className='flex justify-between items-center mb-2'>
                  <p className='h3 text-neutral-800 font-normal mb-0'>{literals.sections}</p>
                  <p className='h1 text-neutral-800 m-0'>{sections?.list?.length}</p>
                </div>
              </div>
              <Button className='mt-6' fullWidth>{literals.common.viewAll}</Button>
            </div>
          </Link>
        </Box>
      ));
    }
    return (
      <>
        <div className='flex justify-between items-center mb-4 carousel-title'>
          <h2 className='m-0'>
            {title}
          </h2>
          <Link to={ROUTE_PATH.PAYMENTS} className='font-bold'>
            {literals.showAll}
          </Link>
        </div>
        <Carousel
          slides={slides}
          showNext
          onSlideChange={onSlideChange}
          breakpoints={{ 992: { slidesPerView: 2, spaceBetween: 16 } }}
          initialSlide={urlParams.get('contract') ? contracts.list.findIndex((c) => c.Numero === urlParams.get('contract')) : 0}
        />
      </>
    );
  };

  return (
    renderItems()
  );
};

export default ContractsAndSections;
